import { getStoredUser } from '../utils/userStorage';

/**
 * Logger Service
 * 
 * A centralized logging service that handles different environments:
 * - Development: Console logging with detailed information
 * - Production: External service integration (Sentry, DataDog, etc.)
 * 
 * Features:
 * - Environment-aware logging
 * - Error aggregation and tracking
 * - User context and session information
 * - Configurable log levels
 * - External service integration ready
 */

class Logger {
  constructor() {
    this.environment = process.env.NODE_ENV || 'development';
    this.isDevelopment = this.environment === 'development';
    this.isProduction = this.environment === 'production';
    
    // Initialize external services in production
    if (this.isProduction) {
      this.initializeExternalServices();
    }
  }

  /**
   * Initialize external logging services (Sentry, DataDog, etc.)
   * Add your production logging service configuration here
   */
  initializeExternalServices() {
    // Example: Initialize Sentry
    // import * as Sentry from '@sentry/browser';
    // Sentry.init({
    //   dsn: process.env.REACT_APP_SENTRY_DSN,
    //   environment: this.environment,
    //   tracesSampleRate: 1.0,
    // });

    // Example: Initialize DataDog
    // import { datadogLogs } from '@datadog/browser-logs';
    // datadogLogs.init({
    //   clientToken: process.env.REACT_APP_DATADOG_CLIENT_TOKEN,
    //   site: 'datadoghq.com',
    //   forwardErrorsToLogs: true,
    //   sessionSampleRate: 100,
    // });

    console.log('📊 External logging services initialized for production');
  }

  /**
   * Get user context for logging
   */
  getUserContext() {
    try {
      const user = getStoredUser();
      return {
        userId: user?.id || 'anonymous',
        email: user?.email || 'unknown',
        timestamp: new Date().toISOString(),
        userAgent: navigator.userAgent,
        url: window.location.href,
        sessionId: this.getSessionId()
      };
    } catch (error) {
      return {
        userId: 'anonymous',
        email: 'unknown',
        timestamp: new Date().toISOString(),
        userAgent: navigator.userAgent,
        url: window.location.href,
        sessionId: this.getSessionId()
      };
    }
  }

  /**
   * Generate or get session ID
   */
  getSessionId() {
    let sessionId = sessionStorage.getItem('logger_session_id');
    if (!sessionId) {
      sessionId = `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      sessionStorage.setItem('logger_session_id', sessionId);
    }
    return sessionId;
  }

  /**
   * Format error for logging
   */
  formatError(message, error, context = {}) {
    const userContext = this.getUserContext();
    
    return {
      message,
      error: {
        name: error?.name || 'Unknown Error',
        message: error?.message || 'No error message',
        stack: error?.stack || 'No stack trace',
        ...error
      },
      context: {
        ...userContext,
        ...context
      },
      level: 'error',
      timestamp: new Date().toISOString()
    };
  }

  /**
   * Log error messages
   */
  error(message, error = null, context = {}) {
    const logData = this.formatError(message, error, context);

    if (this.isDevelopment) {
      // Development: Enhanced console logging
      console.group(`🚨 ERROR: ${message}`);
      console.error('Message:', message);
      if (error) {
        console.error('Error Object:', error);
        if (error.stack) {
          console.error('Stack Trace:', error.stack);
        }
      }
      console.error('Context:', context);
      console.error('User Context:', logData.context);
      console.groupEnd();
    }

    if (this.isProduction) {
      // Production: Send to external services
      this.sendToExternalServices(logData);
    }

    // Always log to console as fallback
    console.error(`[${logData.timestamp}] ${message}`, error);
  }

  /**
   * Log warning messages
   */
  warn(message, context = {}) {
    const logData = {
      message,
      context: {
        ...this.getUserContext(),
        ...context
      },
      level: 'warning',
      timestamp: new Date().toISOString()
    };

    if (this.isDevelopment) {
      console.warn(`⚠️ WARNING: ${message}`, context);
    }

    if (this.isProduction) {
      this.sendToExternalServices(logData);
    }

    console.warn(`[${logData.timestamp}] ${message}`, context);
  }

  /**
   * Log info messages
   */
  info(message, context = {}) {
    const logData = {
      message,
      context: {
        ...this.getUserContext(),
        ...context
      },
      level: 'info',
      timestamp: new Date().toISOString()
    };

    if (this.isDevelopment) {
      console.info(`ℹ️ INFO: ${message}`, context);
    }

    if (this.isProduction) {
      this.sendToExternalServices(logData);
    }

    console.info(`[${logData.timestamp}] ${message}`, context);
  }

  /**
   * Log debug messages (development only)
   */
  debug(message, context = {}) {
    if (this.isDevelopment) {
      console.debug(`🐛 DEBUG: ${message}`, context);
    }
  }

  /**
   * Send logs to external services in production
   */
  sendToExternalServices(logData) {
    try {
      // Example: Send to Sentry
      // if (window.Sentry) {
      //   window.Sentry.captureException(new Error(logData.message), {
      //     extra: logData.context,
      //     level: logData.level
      //   });
      // }

      // Example: Send to DataDog
      // if (window.DD_LOGS) {
      //   window.DD_LOGS.logger[logData.level](logData.message, logData.context);
      // }

      // Example: Send to custom API endpoint
      // fetch('/api/logs', {
      //   method: 'POST',
      //   headers: { 'Content-Type': 'application/json' },
      //   body: JSON.stringify(logData)
      // }).catch(err => console.error('Failed to send log to API:', err));

      // For now, just store in localStorage for debugging in production
      this.storeLogLocally(logData);

    } catch (error) {
      console.error('Failed to send log to external services:', error);
    }
  }

  /**
   * Store logs locally for debugging (production fallback)
   */
  storeLogLocally(logData) {
    try {
      const logs = JSON.parse(localStorage.getItem('app_logs') || '[]');
      logs.push(logData);
      
      // Keep only last 100 logs to prevent storage overflow
      if (logs.length > 100) {
        logs.splice(0, logs.length - 100);
      }
      
      localStorage.setItem('app_logs', JSON.stringify(logs));
    } catch (error) {
      console.error('Failed to store log locally:', error);
    }
  }

  /**
   * Get stored logs (for debugging)
   */
  getLogs() {
    try {
      return JSON.parse(localStorage.getItem('app_logs') || '[]');
    } catch (error) {
      console.error('Failed to retrieve logs:', error);
      return [];
    }
  }

  /**
   * Clear stored logs
   */
  clearLogs() {
    try {
      localStorage.removeItem('app_logs');
      sessionStorage.removeItem('logger_session_id');
      console.log('📝 Logs cleared');
    } catch (error) {
      console.error('Failed to clear logs:', error);
    }
  }

  /**
   * Export logs for support/debugging
   */
  exportLogs() {
    const logs = this.getLogs();
    const exportData = {
      timestamp: new Date().toISOString(),
      environment: this.environment,
      userAgent: navigator.userAgent,
      url: window.location.href,
      logs
    };

    const blob = new Blob([JSON.stringify(exportData, null, 2)], { 
      type: 'application/json' 
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `app-logs-${Date.now()}.json`;
    a.click();
    URL.revokeObjectURL(url);
  }
}

// Create singleton instance
const logger = new Logger();

export default logger;

// Export methods for direct usage
export const { error, warn, info, debug } = logger;
